<template>
  <v-card class="mass-roll-call-shell" flat>
    <v-card-text class="mass-roll-call-content">
      <v-container class="pa-0" fluid>
        <!-- 3.1 Disclaimer -->
        <v-row>
          <v-col cols="12">
            <v-alert type="warning" dense outlined class="mb-2">
              El pase de lista masivo sólo puede ser ejecutado una vez por día. Posterior a la primer ejecución,
              utilizar la edición o registro de asistencias por cada elemento.
            </v-alert>
          </v-col>
        </v-row>

        <v-row v-if="checkingRegistration">
          <v-col cols="12">
            <v-progress-linear indeterminate color="secondary" rounded />
          </v-col>
        </v-row>

        <v-row v-if="alreadyRegistered && !checkingRegistration">
          <v-col cols="12">
            <v-alert type="info" dense outlined class="mb-2">
              El pase de lista masivo ya fue ejecutado el día de hoy. Para modificar asistencias, utilice la edición o
              registro individual por cada elemento.
            </v-alert>
          </v-col>
        </v-row>

        <template v-if="!alreadyRegistered && !checkingRegistration">
          <v-row class="mb-2">
            <v-col cols="12">
              <div class="d-flex flex-wrap align-center">
                <span class="text-caption mr-2" style="color: rgba(224, 224, 224, 0.7)"
                  >Rellenar tipo de asistencia:</span
                >
                <v-btn
                  v-for="tipo in tiposAsistencia"
                  :key="tipo.id"
                  small
                  outlined
                  color="secondary"
                  class="mr-1 mb-1"
                  @click="rellenarTipoAsistencia(tipo.id)"
                >
                  {{ tipo.title }}
                </v-btn>
              </div>
            </v-col>
          </v-row>

          <v-row>
            <v-col cols="12">
              <v-card class="table-shell" color="primary" :loading="loadingPersonal">
                <v-card-text class="table-content">
                  <div class="table-scroll-wrapper">
                    <v-data-table
                      :headers="headers"
                      :items="rows"
                      :items-per-page="20"
                      :footer-props="{
                        'items-per-page-options': [20, 50, 100, 150],
                        'items-per-page-text': 'Filas por página',
                      }"
                      class="transparent mass-table"
                      item-key="id_personal"
                    >
                      <template v-slot:[`item.personal`]="{ item }">
                        <div class="person-name">{{ item.nombreCompleto }}</div>
                        <div class="person-cuip">{{ item.CUIP || 'Sin CUIP' }}</div>
                      </template>

                      <template v-slot:[`item.tipoAsistencia`]="{ item }">
                        <v-select
                          v-model="item.tipoAsistencia"
                          :items="tiposAsistencia"
                          item-value="id"
                          item-text="title"
                          item-color="secondary"
                          label="Tipo"
                          filled
                          rounded
                          dense
                          hide-details
                          color="secondary"
                          background-color="primary"
                          class="inline-input"
                          style="min-width: 160px"
                        />
                      </template>

                      <template v-slot:[`item.servicio`]="{ item }">
                        <v-select
                          v-model="item.servicio"
                          :items="tiposServicio"
                          item-value="id"
                          item-text="title"
                          item-color="secondary"
                          label="Servicio"
                          filled
                          rounded
                          dense
                          hide-details
                          color="secondary"
                          background-color="primary"
                          class="inline-input"
                          style="min-width: 160px"
                        />
                      </template>

                      <template v-slot:[`item.crpUnidad`]="{ item }">
                        <v-text-field
                          v-model="item.crpUnidad"
                          label="CRP/Unidad"
                          filled
                          rounded
                          dense
                          hide-details
                          color="secondary"
                          background-color="primary"
                          class="inline-input"
                          style="min-width: 120px"
                        />
                      </template>

                      <template v-slot:[`item.radio`]="{ item }">
                        <v-text-field
                          v-model="item.radio"
                          label="Radio"
                          filled
                          rounded
                          dense
                          hide-details
                          color="secondary"
                          background-color="primary"
                          class="inline-input"
                          style="min-width: 100px"
                        />
                      </template>

                      <template v-slot:[`item.sector`]="{ item }">
                        <v-text-field
                          v-model="item.sector"
                          label="Sector"
                          filled
                          rounded
                          dense
                          hide-details
                          color="secondary"
                          background-color="primary"
                          class="inline-input"
                          style="min-width: 100px"
                        />
                      </template>

                      <template v-slot:[`item.hora`]="{ item }">
                        <v-text-field
                          v-model="item.hora"
                          v-mask="'##:##'"
                          label="Hora"
                          placeholder="00:00"
                          persistent-placeholder
                          maxlength="5"
                          filled
                          rounded
                          dense
                          hide-details
                          color="secondary"
                          background-color="primary"
                          class="inline-input"
                          style="min-width: 150px"
                          append-icon="mdi-clock-outline"
                        />
                      </template>
                    </v-data-table>
                  </div>
                </v-card-text>
              </v-card>
            </v-col>
          </v-row>

          <v-row class="mt-4">
            <v-col cols="12" class="d-flex justify-center">
              <v-btn
                color="secondary"
                large
                rounded
                :disabled="!allRowsComplete || loading"
                :loading="loading"
                @click="ejecutarPaseListaMasivo"
              >
                <v-icon left>mdi-clipboard-check-multiple-outline</v-icon>
                Ejecutar pase de lista masivo
              </v-btn>
            </v-col>
          </v-row>
        </template>
      </v-container>
    </v-card-text>
  </v-card>
</template>

<script>
import moment from 'moment'
import { mapActions, mapGetters, mapMutations } from 'vuex'

const ID_SEDE_SSC = 1

export default {
  name: 'MassRollCallSection',

  data() {
    return {
      headers: [
        { text: 'Personal', value: 'personal', width: '220px' },
        { text: 'Tipo de asistencia', value: 'tipoAsistencia', width: '180px', sortable: false },
        { text: 'Servicio', value: 'servicio', width: '180px', sortable: false },
        { text: 'CRP/Unidad', value: 'crpUnidad', width: '140px', sortable: false },
        { text: 'Radio', value: 'radio', width: '120px', sortable: false },
        { text: 'Sector', value: 'sector', width: '120px', sortable: false },
        { text: 'Hora', value: 'hora', width: '160px', sortable: false },
      ],
      rows: [],
      loadingPersonal: false,
      loading: false,
      idSede: null,
      idSubsede: null,
      alreadyRegistered: false,
      checkingRegistration: false,
    }
  },

  computed: {
    ...mapGetters('asistencia', {
      tiposAsistencia: 'getTiposAsistencia',
      tiposServicio: 'getTiposServicio',
    }),
    ...mapGetters({
      idPersonal: 'getIdPersonal',
    }),

    allRowsComplete() {
      if (!this.rows.length) return false
      return this.rows.every((row) => {
        const horaValida = row.hora && row.hora.length === 5
        return row.tipoAsistencia && row.servicio && horaValida
      })
    },
  },

  async mounted() {
    await Promise.all([
      this.$store.dispatch('asistencia/fetchTipoAsistencia'),
      this.$store.dispatch('asistencia/fetchTipoServicio'),
    ])
    await this.loadPersonalBySede()
    await this.checkIfAlreadyRegistered()
  },

  methods: {
    ...mapActions('personal', ['fetchSedePersonal']),
    ...mapMutations(['SET_SNACKBAR_MESSAGE']),

    async checkIfAlreadyRegistered() {
      if (!this.rows.length) return
      this.checkingRegistration = true
      try {
        const firstId = this.rows[0].id_personal
        this.alreadyRegistered = await this.$store.dispatch('asistencia/verificarAsistenciaHoy', firstId)
      } catch {
        this.alreadyRegistered = false
      } finally {
        this.checkingRegistration = false
      }
    },

    rellenarTipoAsistencia(idTipo) {
      this.rows = this.rows.map((row) => ({ ...row, tipoAsistencia: idTipo }))
    },

    async loadPersonalBySede() {
      this.loadingPersonal = true
      try {
        const { idSede, idSubsede } = await this.fetchSedePersonal(this.idPersonal)
        this.idSede = idSede
        this.idSubsede = idSubsede

        if (!idSede) {
          this.SET_SNACKBAR_MESSAGE('No se encontró información de sede. Por favor, contacte al administrador.')
          return
        }

        await this.$store.dispatch('sede/fetchSedes')

        let response = null
        if (idSede === ID_SEDE_SSC && idSubsede) {
          await this.$store.dispatch('subsede/fetchSubsedes')
          response = await this.$store.dispatch('sede/fetchPersonalBySubSede', {
            pageNumber: 1,
            rowsOfPage: 150,
            idSubSede: idSubsede,
          })
        } else {
          response = await this.$store.dispatch('sede/fetchPersonalBySede', {
            pageNumber: 1,
            rowsOfPage: 150,
            idSede,
          })
        }

        const personal = response?.data && Array.isArray(response.data) ? response.data : []
        this.rows = personal.map((p) => ({
          id_personal: p.id_personal,
          nombreCompleto: p.nombreCompleto,
          CUIP: p.CUIP,
          tipoAsistencia: null,
          servicio: null,
          crpUnidad: '',
          radio: '',
          sector: '',
          hora: '',
        }))
      } catch {
        this.SET_SNACKBAR_MESSAGE('Error al cargar el personal.')
      } finally {
        this.loadingPersonal = false
      }
    },

    async ejecutarPaseListaMasivo() {
      this.loading = true
      try {
        const fechaBase = moment().format('YYYY/MM/DD')
        const promises = this.rows.map((row) => {
          const hora = row.hora || moment().format('HH:mm')
          const payload = {
            fk_idPersonal: row.id_personal,
            fk_idTipoAsistencia: row.tipoAsistencia,
            fecha: `${fechaBase} ${hora}`,
            fk_idTipoServio: row.servicio,
            fk_idTipoServicio: row.servicio,
            crp: row.crpUnidad || null,
            crpUnidad: row.crpUnidad || null,
            radio: row.radio || null,
            sector: row.sector || null,
            fk_idSubsede: this.idSubsede || null,
          }
          return this.$store.dispatch('asistencia/saveAsistencia', {
            idPersonal: row.id_personal,
            asistencia: payload,
          })
        })

        const results = await Promise.allSettled(promises)
        const saved = results.filter((r) => r.status === 'fulfilled').length
        const failed = results.filter((r) => r.status === 'rejected').length

        if (failed === 0) {
          this.SET_SNACKBAR_MESSAGE(`${saved} registros guardados correctamente.`)
        } else {
          this.SET_SNACKBAR_MESSAGE(`${saved} registros guardados, ${failed} con error.`)
        }
        this.alreadyRegistered = true
      } finally {
        this.loading = false
      }
    },
  },
}
</script>

<style scoped>
.mass-roll-call-shell {
  background: transparent !important;
  border: 0 !important;
  box-shadow: none !important;
}

::v-deep .mass-roll-call-shell.v-card {
  background: transparent !important;
  border: 0 !important;
  box-shadow: none !important;
}

::v-deep .mass-roll-call-shell.v-card:hover {
  transform: none !important;
  box-shadow: none !important;
}

.mass-roll-call-content {
  padding: 24px !important;
}

.table-shell {
  background: rgba(12, 19, 24, 0.38) !important;
  border: 1px solid rgba(var(--v-secondary-base-rgb), 0.14) !important;
  border-radius: 20px !important;
  box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.03), 0 12px 30px rgba(0, 0, 0, 0.2) !important;
}

.table-content {
  padding: 12px !important;
}

/* 7.3 Horizontal scroll container */
.table-scroll-wrapper {
  overflow-x: auto;
}

.person-name {
  color: #f4f4f4;
  font-weight: 700;
  white-space: nowrap;
}

.person-cuip {
  color: rgba(224, 224, 224, 0.6);
  font-size: 0.78rem;
  white-space: nowrap;
}

::v-deep .mass-table {
  background: transparent !important;
}

/* 8.2 / 8.3 Blue text and uniform background for all column headers */
::v-deep .mass-table thead tr th {
  color: var(--v-secondary-base) !important;
  background: #0d1b26 !important;
  border-bottom: 1px solid rgba(var(--v-secondary-base-rgb, 69, 170, 242), 0.14) !important;
}

::v-deep .mass-table tbody tr:first-child td {
  padding-top: 20px !important;
}

::v-deep .mass-table thead tr th,
::v-deep .mass-table tbody tr td {
  padding-left: 16px !important;
  padding-right: 16px !important;
}

/* 7.4 Sticky first column — header */
::v-deep .mass-table thead tr th:first-child {
  position: sticky !important;
  left: 0 !important;
  z-index: 4 !important;
  background: #0d1b26 !important;
}

/* 7.4 Sticky first column — body cells */
::v-deep .mass-table tbody tr td:first-child {
  position: sticky !important;
  left: 0 !important;
  z-index: 2 !important;
  background: #0d1b26 !important;
}

::v-deep .inline-input {
  margin-right: 1px !important;
  margin-top: 6px !important;
  margin-bottom: 6px !important;
}

::v-deep .inline-input .v-input__slot {
  background: rgba(8, 14, 19, 0.5) !important;
  border: 1px solid rgba(var(--v-secondary-base-rgb, 69, 170, 242), 0.2) !important;
  border-radius: 10px !important;
}

::v-deep .inline-input .v-label,
::v-deep .inline-input input,
::v-deep .inline-input .v-icon {
  color: #e0f0ff !important;
}

::v-deep .inline-input.v-input--is-focused .v-input__slot {
  border-color: var(--v-secondary-base) !important;
}

::v-deep .v-data-table__empty-wrapper {
  color: rgba(224, 224, 224, 0.78) !important;
}

::v-deep .v-data-footer {
  color: rgba(224, 224, 224, 0.85) !important;
  margin-top: 20px !important;
  padding-top: 16px !important;
  border-top: 1px solid rgba(var(--v-secondary-base-rgb, 69, 170, 242), 0.14) !important;
}

::v-deep .v-data-footer .v-icon {
  color: rgba(224, 224, 224, 0.85) !important;
}

::v-deep .v-data-footer .v-select__selection {
  color: rgba(224, 224, 224, 0.85) !important;
}
</style>
